/**
 * Redux slice for the Audit Workspace page.
 *
 * Follows the same pattern as auditQueueSlice.ts (createAsyncThunk + extraReducers).
 *
 * The workspace task endpoint returns the task, its use-case config (fields,
 * checklist, comparison_mode — looked up by use_case_id from the config DB),
 * any existing result for the current auditor, and the full task list for
 * progress tracking.
 *
 * When AUDIT_V2_ENABLED is true (types.ts), thunks return sample data.
 * When the backend API is ready, thunks call auditModuleService.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AuditModuleService from "src/services/auditModuleService";
import {
  SAMPLE_WORKSPACE_TASKS,
  SAMPLE_WORKSPACE_RESULTS,
  SAMPLE_USE_CASE_CONFIGS,
  SAMPLE_AUDITOR_ALIAS,
} from "src/components/pages/auditModule/shared/sampleData";
import {
  AUDIT_V2_ENABLED,
  WorkspaceTask,
  WorkspaceResult,
  UseCaseConfig,
  FieldDecision,
  ChecklistResponseValue,
} from "src/components/pages/auditModule/shared/types";

// ----------------------------  Types  ----------------------------

export interface AuditWorkspaceState {
  task: WorkspaceTask | null;
  config: UseCaseConfig | null;
  result: WorkspaceResult | null;
  allTasks: WorkspaceTask[];
  /** Remembers the last opened task so the sidebar link stays active after navigating away. */
  lastActiveTaskId: string | null;
  getTaskLoading: LOADING_STATE;
  saveResultLoading: LOADING_STATE;
}

const initialState: AuditWorkspaceState = {
  task: null,
  config: null,
  result: null,
  allTasks: [],
  lastActiveTaskId: null,
  getTaskLoading: "idle",
  saveResultLoading: "idle",
};

// ----------------------------  Async Thunks  ----------------------------

export const getWorkspaceTask = createAsyncThunk(
  "auditWorkspace/getTask",
  async ({ taskId, currentAlias }: { taskId: string; currentAlias: string }) => {
    if (AUDIT_V2_ENABLED) {
      // Replace sample auditor alias with current user
      const tasks = SAMPLE_WORKSPACE_TASKS.map((t) =>
        t.assigned_to === SAMPLE_AUDITOR_ALIAS ? { ...t, assigned_to: currentAlias } : t,
      );
      const task = tasks.find((t) => t.id === taskId) ?? null;
      const config = task
        ? SAMPLE_USE_CASE_CONFIGS.find((c) => c.id === task.use_case_id) ?? null
        : null;
      const results = SAMPLE_WORKSPACE_RESULTS.map((r) =>
        r.auditor_id === SAMPLE_AUDITOR_ALIAS ? { ...r, auditor_id: currentAlias } : r,
      );
      const result = task
        ? results.find((r) => r.task_id === task.id && r.auditor_id === currentAlias) ?? null
        : null;
      return { task, config, result, allTasks: tasks };
    }
    return AuditModuleService.getWorkspaceTask(taskId);
  },
);

export const saveWorkspaceResult = createAsyncThunk(
  "auditWorkspace/saveResult",
  async (payload: { result: WorkspaceResult; submit: boolean }) => {
    if (AUDIT_V2_ENABLED) {
      return {
        ...payload.result,
        status: payload.submit ? ("submitted" as const) : ("draft" as const),
      };
    }
    return AuditModuleService.saveWorkspaceResult(payload.result, payload.submit);
  },
);

// ----------------------------  Slice  ----------------------------

const { reducer, actions } = createSlice({
  name: "auditWorkspaceSlice",
  initialState,
  reducers: {
    resetWorkspace: (state) => ({
      ...initialState,
      allTasks: state.allTasks,
      lastActiveTaskId: state.lastActiveTaskId,
    }),
    resetSaveResultStatus: (state) => {
      state.saveResultLoading = "idle";
    },
    updateLocalDecisions: (state, action: PayloadAction<Record<string, FieldDecision>>) => {
      if (state.result) {
        state.result.decisions = action.payload;
      }
    },
    updateLocalChecklist: (
      state,
      action: PayloadAction<Record<string, ChecklistResponseValue>>,
    ) => {
      if (state.result) {
        state.result.checklist_responses = action.payload;
      }
    },
    updateLocalFlagged: (state, action: PayloadAction<boolean>) => {
      if (state.result) {
        state.result.flagged = action.payload;
      }
    },
    updateLocalComments: (state, action: PayloadAction<string>) => {
      if (state.result) {
        state.result.comments = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWorkspaceTask.pending, (state) => {
      state.getTaskLoading = "pending";
    });
    builder.addCase(getWorkspaceTask.fulfilled, (state, { payload }) => {
      state.task = payload.task;
      state.config = payload.config;
      state.result = payload.result ?? {
        id: "wres-" + crypto.randomUUID().replace(/-/g, "").slice(0, 12),
        task_id: payload.task?.id ?? "",
        auditor_id: "",
        status: "draft",
        decisions: {},
        checklist_responses: {},
        flagged: false,
        comments: "",
      };
      // Merge incoming tasks with existing completed/expired statuses so progress is preserved
      const incoming = payload.allTasks ?? [];
      const existingStatusMap = new Map(state.allTasks.map((t) => [t.id, t.status]));
      state.allTasks = incoming.map((t) => {
        const prev = existingStatusMap.get(t.id);
        return prev === "completed" || prev === "expired" ? { ...t, status: prev } : t;
      });
      state.lastActiveTaskId = payload.task?.id ?? state.lastActiveTaskId;
      state.getTaskLoading = "fulfilled";
    });
    builder.addCase(getWorkspaceTask.rejected, (state) => {
      state.getTaskLoading = "rejected";
    });

    builder.addCase(saveWorkspaceResult.pending, (state) => {
      state.saveResultLoading = "pending";
    });
    builder.addCase(saveWorkspaceResult.fulfilled, (state, { payload }) => {
      state.result = payload;
      // When a result is submitted, mark the corresponding task as completed
      if (payload.status === "submitted") {
        state.allTasks = state.allTasks.map((t) =>
          t.id === payload.task_id ? { ...t, status: "completed" as const } : t,
        );
      }
      state.saveResultLoading = "fulfilled";
    });
    builder.addCase(saveWorkspaceResult.rejected, (state) => {
      state.saveResultLoading = "rejected";
    });
  },
});

export const {
  resetWorkspace,
  resetSaveResultStatus,
  updateLocalDecisions,
  updateLocalChecklist,
  updateLocalFlagged,
  updateLocalComments,
} = actions;

export default reducer;
